import { supabase } from '../supabase.js';
import { coreState } from './core.js';
import { Shop } from '../models/Shop.js';
import { ShopMember } from '../models/ShopMember.js';
import { Profile } from '../models/Profile.js';
import { Notification } from '../models/Notification.js';
import { createNotificationFromTemplate } from '../utils/notificationTemplates.js';
import { coordinateUtils } from '../utils/coordinateUtils.js';
import { useSitesStore } from './useSitesStore.js';
import { useAuth } from './useAuth.js';
import { useNetworkStore } from './useNetworkStore.js';
import { offlineStorage } from '../services/offlineStorage.js';
import { buildOfflinePayload, shouldSyncLog, buildSyncPayload, buildOfflineFeedItems } from './offlineSyncLogic.js';

export const useShopDashboard = () => {

    const auth = useAuth(); // [NEW] Access Auth Store

    // --- Core Loading ---
    // `prefetched` (optional): a Promise resolving to the raw edge-fn payload,
    // typically kicked off from index.html before the SPA bundle finished
    // loading. If provided and resolves cleanly, we skip the duplicate invoke.
    async function loadShopWidgetData(shopId, host = null, token = null, prefetched = null) {
        console.log("[ShopDashboard] Loading Widget Data for:", shopId, "Token:", token, "Host:", host, "Prefetched:", !!prefetched);

        const wrap = (data) => ({
            shop: new Shop(data.shop),
            diveSites: data.diveSites,
            logs: data.logs,
            team: data.team,
            debug: data.debug,
            distributorStyle: data.distributorStyle
        });

        if (prefetched) {
            try {
                const data = await prefetched;
                if (data && data.shop) {
                    console.log("[ShopDashboard] Using prefetched payload");
                    return wrap(data);
                }
            } catch (e) {
                // HTTP error (the server already rejected): re-throw directly so
                // we don't waste a duplicate invoke. Network failures (no
                // status) fall through to the SDK retry below.
                if (e && e.status) {
                    const enriched = new Error(e.body?.message || e.message || 'Widget load failed');
                    enriched.status = e.status;
                    enriched.customMessage = e.body?.message;
                    enriched.action = e.body?.action;
                    throw enriched;
                }
                console.warn("[ShopDashboard] Prefetch network error, falling back to invoke:", e);
            }
        }

        try {
            const body = { host };
            if (token) {
                body.token = token;
            } else {
                body.shopId = shopId;
            }
            const { data, error } = await supabase.functions.invoke('swift-worker', { body });
            if (error) {
                console.error("[ShopDashboard] Invoke Error Details:", error);
                // Attempt to read the error body if available
                if (error && error.context && typeof error.context.json === 'function') {
                    try {
                        const errBody = await error.context.json();
                        console.error("[ShopDashboard] Invoke Error Body:", errBody);

                        // Enrich the error object with custom fields
                        if (errBody.message) error.customMessage = errBody.message;
                        if (errBody.action) error.action = errBody.action;
                        error.status = 403; // Explicitly set status for UI handling

                    } catch (e) {
                        console.warn("[ShopDashboard] Could not parse error body:", e);
                    }
                }
                throw error;
            }
            console.log("[ShopDashboard] Widget Data Loaded:", data);
            return wrap(data);
        } catch (e) {
            console.error("Widget Data Load Failed:", e);
            throw e;
        }
    }

    async function rankSites(shopId) {
        console.log("[ShopDashboard] Ranking Sites for:", shopId);
        try {
            const { data, error } = await supabase.functions.invoke('rank-sites', { body: { shopId } });
            if (error) throw error;
            return data.rankedSites; // Array of { id, name, last_log_at }
        } catch (e) {
            console.error("Site Ranking Failed:", e);
            return [];
        }
    }

    // fetchSites MOVED to useSitesStore.js


    // Moved to stores/useNetworkStore.js


    const networkStore = useNetworkStore(); // [NEW] Access Network Store

    // --- Feed / Logs ---

    // 1. Radar Feed (Geospatial)
    async function fetchRadarLogs(lat, lng, radiusKm = 50) {
        const { data, error } = await supabase.rpc('get_nearby_logs', {
            query_lat: lat,
            query_lng: lng,
            radius_km: radiusKm,
            limit_count: 20
        });

        if (error) {
            console.error("[ShopDashboard] Radar Fetch Error:", error);
            return [];
        }

        // Enrich with source type
        return data.map(log => ({
            ...log,
            isRadar: true
            // Real profiles and shops data is now populated by the RPC
        }));
    }

    async function fetchFeed(userId = null, userLocation = null) {
        // [OFFLINE GUARD] If offline, skip all Supabase queries and show queued logs from IndexedDB
        if (!navigator.onLine) {
            console.log('[ShopDashboard] Offline — loading feed from IndexedDB only');
            const pendingLogs = await offlineStorage.getPendingLogs();
            const offlineRawLogs = buildOfflineFeedItems(pendingLogs, coreState.activeUser);

            if (offlineRawLogs.length > 0) {
                const { useFeedAggregator } = await import('../composables/useFeedAggregator.js');
                const aggregator = useFeedAggregator();
                coreState.feedItems = aggregator.aggregateLogs(offlineRawLogs, new Set(), {
                    currentUserId: coreState.activeUser?.id,
                    currentShopId: coreState.currentShopId
                });
            } else {
                // Keep existing feed items (cached from last online session) if no pending logs
                // Only clear if there was nothing before either
                if (!coreState.feedItems || coreState.feedItems.length === 0) {
                    coreState.feedItems = [];
                }
            }
            return;
        }

        // [FIX] Ensure Session Validity - bail out if session is invalid
        /* check auth... */

        // 1. Fetch Hidden Logs if in Shop Context
        const hiddenLogIds = new Set();
        if (coreState.currentShopId && !userId) {
            const { data: hidden } = await supabase
                .from('shop_hidden_logs')
                .select('log_id')
                .eq('shop_id', coreState.currentShopId);
            hidden?.forEach(h => hiddenLogIds.add(h.log_id));
        }

        let rawLogs = [];

        if (userId) {
            // A. Profile View: Fetch specific user logs
            const { data, error } = await supabase.from('logs')
                .select(`*, profiles:user_id (name, avatar, qualifications, venmo_handle), sites:site_id (name), shops:shop_id (name)`)
                .eq('user_id', userId)
                .order('created_at', { ascending: false });
            
            if (error) {
                console.warn("[ShopDashboard] Profile feed fetch error:", error);
                auth.handleAuthError(error);
                // Keep rawLogs as empty if no cache, offlineRawLogs will be prepended later
            } else {
                rawLogs = data;
            }

        } else if (coreState.currentShopId) {
            // B. Dashboard View: Unified Feed (Shop + Partners + Radar)

            // 1. My Shop Logs
            const p1 = supabase.from('logs')
                .select(`*, profiles:user_id (name, avatar, qualifications, venmo_handle), sites:site_id (name), shops:shop_id (name)`)
                .eq('shop_id', coreState.currentShopId)
                .order('created_at', { ascending: false })
                .limit(20);

            // 2. Partner Logs
            // Fetch partners first
            const partners = await networkStore.fetchShopSources(coreState.currentShopId);
            const partnerIds = partners.map(p => p.id);

            let p2 = Promise.resolve({ data: [] });
            if (partnerIds.length > 0) {
                p2 = supabase.from('logs')
                    .select(`*, profiles:user_id (name, avatar, qualifications, venmo_handle), sites:site_id (name), shops:shop_id (name)`)
                    .in('shop_id', partnerIds)
                    .order('created_at', { ascending: false })
                    .limit(20);
            }

            // 3. Radar Logs
            let p3 = Promise.resolve({ data: [] });
            if (userLocation && userLocation.lat && userLocation.lng) {
                // Note: RPC returns a flat structure, might need mapping to match `logs` select shape
                // For now, let's assume we map it manually or the RPC matches close enough
                // The RPC returns specific columns. We need to handle the data shape difference.
                // Actually, let's call the RPC and map the result to match the 'logs' shape for the aggregator.
                p3 = fetchRadarLogs(userLocation.lat, userLocation.lng);
            }

            const [res1, res2, res3] = await Promise.all([p1, p2, p3]);

            // Merge results. Handle potential errors by not failing completely.
            let shopLogs = [];
            if (res1.error) {
                console.warn("[ShopDashboard] Shop feed fetch error:", res1.error);
                auth.handleAuthError(res1.error);
                // [CACHING GRACEFUL UPGRADE] If we have a cached feed and the token just died,
                // we might want to return early and keep the old feed, just appending offline ones.
                // For now, let's keep it simple: if network fails, we won't overwrite feedItems later
                // IF rawLogs stays empty, we check before aggregation.
            } else {
                shopLogs = res1.data || [];
            }
            
            const partnerLogs = (res2.data || []).map(l => ({ ...l, isPartner: true }));

            // Map Radar RPC results...
            const radarLogs = (Array.isArray(res3) ? res3 : []).map(r => {
                const profiles = typeof r.profiles === 'string' ? JSON.parse(r.profiles) : r.profiles;
                const shops = typeof r.shops === 'string' ? JSON.parse(r.shops) : r.shops;
                return {
                    ...r,
                    isRadar: true,
                    profiles: profiles || { name: 'Unknown Diver', avatar: '' },
                    shops: shops || undefined,
                    sites: { name: r.site_name }
                };
            });

            // Radar first so partner/shop logs overwrite during dedup (Map keeps last entry)
            rawLogs = [...radarLogs, ...partnerLogs, ...shopLogs];
        } else {
            // C. Fallback (No context)
            console.warn("[ShopDashboard] fetchFeed called without UserID or ShopID context.");
            return;
        }

        // [NEW] Inject offline logs
        const pendingLogs = await offlineStorage.getPendingLogs();
        if (pendingLogs.length > 0) {
            const offlineRawLogs = pendingLogs.map(p => ({
                id: p.id,
                user_id: coreState.activeUser?.id,
                shop_id: p.shopId,
                site_id: p.siteId,
                created_at: p.timestamp,
                content: p.posts?.[0]?.content || '',
                images: p.posts?.[0]?.images || [],
                conditions: p.conditions,
                sightings: p.sightings,
                privacy: 'public',
                isOffline: true,
                profiles: {
                    name: coreState.activeUser?.name || 'Me',
                    avatar: coreState.activeUser?.avatar || ''
                }
            }));

            if (!userId || userId === coreState.activeUser?.id) {
                // If shop feed, optionally filter by shopId
                // But generally offline logs belong to the active user in the current app session
                rawLogs = [...offlineRawLogs, ...rawLogs];
            }
        }

        // [GRACEFUL DEGRADATION] If online fetch returned nothing (likely due to auth error/network),
        // and we have an existing cached feed but NO offline logs, we should preserve the cache.
        // If we DO have offline logs, we still need to run the aggregator to inject them into the feed,
        // but we want to merge them with the existing cache, not an empty rawLogs array.
        if (rawLogs.length === 0 && (!pendingLogs || pendingLogs.length === 0)) {
            if (coreState.feedItems && coreState.feedItems.length > 0) {
                 console.log("[ShopDashboard] Preserving cached feed items after fetch failure.");
                 return; // Keep existing cache intact
            }
        }
        
        // If we didn't get any online logs but we DO have a cache and new offline logs,
        // we need to feed the previous cached logs BACK into the aggregator along with the new offline ones!
        if (rawLogs.length > 0 && navigator.onLine) {
            // Excellent, we got fresh data. It will replace cache.
        } else if (coreState.feedItems && coreState.feedItems.length > 0) {
            // We failed to get online data or we are merging pending logs into existing cache.
            // Un-aggregate the previous feedItems so they can be re-aggregated with the new offline ones.
            const cachedRawLogs = [];
            coreState.feedItems.forEach(group => {
                group.posts.forEach(post => {
                    cachedRawLogs.push({
                        id: post.id,
                        user_id: post.userId,
                        shop_id: post.shopId,
                        site_id: group.siteId,
                        created_at: post.created_at,
                        content: post.content,
                        images: post.images,
                        conditions: group.conditions,
                        sightings: group.sightings,
                        privacy: post.privacyLevel,
                        isOffline: post.isOffline,
                        isRadar: post.isRadar,
                        isPartner: post.isPartner,
                        isHidden: post.isHidden,
                        profiles: {
                            name: post.author.name,
                            avatar: post.author.avatar,
                            qualifications: post.author.role !== 'Diver' ? [{ agency: post.author.role.split(' ')[0], name: post.author.role.split(' ').slice(1).join(' ') }] : []
                        },
                        sites: { name: group.siteName },
                        shops: { name: group.source.shopName }
                    });
                });
            });
            // Merge cached with the offline injected ones
            rawLogs = [...rawLogs, ...cachedRawLogs];
        }

        // Use the new centralized aggregator for mapping, grouping, and privacy checks
        const { useFeedAggregator } = await import('../composables/useFeedAggregator.js');
        const aggregator = useFeedAggregator();

        coreState.feedItems = aggregator.aggregateLogs(rawLogs, hiddenLogIds, {
            currentUserId: coreState.activeUser?.id,
            currentShopId: coreState.currentShopId
        });
    }

    async function saveLog(logData) {
        // [NEW] Offline Handling (Explicit Check + Fallback)
        let isOnline = navigator.onLine;

        // Double check if navigator says online (it often lies)
        if (isOnline) {
            const { networkUtils } = await import('../utils/networkUtils.js');
            isOnline = await networkUtils.checkConnectivity();
        }

        if (!isOnline) {
            console.log("[ShopDashboard] Device Offline - Saving to IndexedDB");
            return await _saveOffline(logData);
        }

        // ... implementation same as store.js ...
        const payload = {
            user_id: coreState.activeUser.id,
            site_id: logData.siteId,
            shop_id: coreState.currentShopId,
            created_at: logData.timestamp === 'Just now' ? new Date() : (logData.timestamp || new Date()),
            content: logData.posts[0].content,
            images: logData.posts[0].images, // Should be URLs provided by LogDiveForm
            conditions: logData.conditions,
            sightings: logData.sightings,
            privacy: 'public'
        };

        // [SAFE WAKE] Race the insert against a timeout (e.g. 5s)
        try {
            const { networkUtils } = await import('../utils/networkUtils.js');
            const { data, error } = await networkUtils.withTimeout(
                supabase.from('logs').insert(payload).select().single(),
                5000,
                "Save request timed out"
            );
            if (error) throw error;
            fetchFeed();
            return data;

        } catch (e) {
            console.warn("[ShopDashboard] Save failed/timed out despite appearing online. Falling back to offline.", e);
            // Fallback!
            return await _saveOffline(logData);
        }
    }

    // Helper for offline save
    async function _saveOffline(logData) {
        const offlinePayload = buildOfflinePayload(logData, coreState.activeUser?.id, coreState.currentShopId);
        await offlineStorage.saveLog(offlinePayload);
        // Refresh feed to show the new offline log in the UI immediately
        await fetchFeed();
        return offlinePayload;
    }

    async function deleteLog(logId) {
        // Hard delete (only for posts under 48 hours)
        const { error } = await supabase
            .from('logs')
            .delete()
            .eq('id', logId);

        if (error) throw error;

        // Remove from local state for speed
        coreState.feedItems.forEach(group => {
            group.posts = group.posts.filter(p => p.id !== logId);
        });
        // cleanup empty groups
        coreState.feedItems = coreState.feedItems.filter(g => g.posts.length > 0);
    }

    async function anonymizeLog(logId) {
        // Soft delete: Wipe personal content, flag as deleted
        const { error } = await supabase.from('logs').update({
            content: null,
            images: [],
            privacy: 'deleted',
        }).eq('id', logId);

        if (error) throw error;

        // Refresh feed locally
        fetchFeed();
    }

    async function makeLogPrivate(logId) {
        const { error } = await supabase.from('logs').update({ privacy: 'private' }).eq('id', logId);
        if (error) throw error;
        // Refresh feed locally
        fetchFeed();
    }

    async function makeLogPublic(logId) {
        const { error } = await supabase.from('logs').update({ privacy: 'public' }).eq('id', logId);
        if (error) throw error;
        // Refresh feed locally
        fetchFeed();
    }

    // --- Feed Moderation ---
    async function hideLogFromFeed(logIds) {
        if (!coreState.currentShopId) return;
        if (!['Owner', 'Manager'].includes(coreState.currentUserRole)) {
            throw new Error('[RLS Guard] Only Owner/Manager can hide logs from feed.');
        }

        // Ensure logIds is an array
        const idsToHide = Array.isArray(logIds) ? logIds : [logIds];
        if (idsToHide.length === 0) return;

        const inserts = idsToHide.map(id => ({
            shop_id: coreState.currentShopId,
            log_id: id
        }));

        const { error } = await supabase.from('shop_hidden_logs').upsert(inserts, { onConflict: 'shop_id,log_id' });
        if (error) throw error;

        // Force full reactivity for Vue transitions
        const newFeedItems = coreState.feedItems.map(g => {
            const newPosts = g.posts.map(p => idsToHide.includes(p.id) ? { ...p, isHidden: true } : p);
            return { ...g, posts: newPosts };
        });
        coreState.feedItems.splice(0, coreState.feedItems.length, ...newFeedItems);
    }

    async function unhideLogFromFeed(logIds) {
        if (!coreState.currentShopId) return;
        if (!['Owner', 'Manager'].includes(coreState.currentUserRole)) {
            throw new Error('[RLS Guard] Only Owner/Manager can unhide logs from feed.');
        }

        const idsToUnhide = Array.isArray(logIds) ? logIds : [logIds];
        if (idsToUnhide.length === 0) return;

        const { error } = await supabase.from('shop_hidden_logs')
            .delete()
            .eq('shop_id', coreState.currentShopId)
            .in('log_id', idsToUnhide);

        if (error) throw error;

        // Force full reactivity for Vue transitions
        const newFeedItems = coreState.feedItems.map(g => {
            const newPosts = g.posts.map(p => idsToUnhide.includes(p.id) ? { ...p, isHidden: false } : p);
            return { ...g, posts: newPosts };
        });
        coreState.feedItems.splice(0, coreState.feedItems.length, ...newFeedItems);
    }


    async function fetchHiddenLogs() {
        if (!coreState.currentShopId) return [];
        if (!['Owner', 'Manager'].includes(coreState.currentUserRole)) {
            throw new Error('[RLS Guard] Only Owner/Manager can view hidden logs.');
        }

        const { data, error } = await supabase
            .from('shop_hidden_logs')
            .select(`
                id,
                log_id,
                reason,
                created_at,
                logs:log_id (
                    id,
                    content,
                    images,
                    created_at,
                    user_id,
                    site_id,
                    profiles:user_id ( name, avatar ),
                    sites:site_id ( name )
                )
            `)
            .eq('shop_id', coreState.currentShopId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[ShopDashboard] Failed to fetch hidden logs:', error);
            return [];
        }

        return (data || []).map(entry => ({
            id: entry.id,
            logId: entry.log_id,
            reason: entry.reason,
            hiddenAt: entry.created_at,
            log: entry.logs ? {
                id: entry.logs.id,
                content: entry.logs.content,
                images: entry.logs.images || [],
                createdAt: entry.logs.created_at,
                userId: entry.logs.user_id,
                author: {
                    name: entry.logs.profiles?.name || 'Unknown',
                    avatar: entry.logs.profiles?.avatar || ''
                },
                siteName: entry.logs.sites?.name || 'Unknown Site'
            } : null
        }));
    }

    async function syncLogs() {
        if (!navigator.onLine) return; // Basic sanity check
        if (coreState.isSyncing) return; // Prevent overlapping syncs

        const pending = await offlineStorage.getPendingLogs();
        if (pending.length === 0) return;

        // [PRE-SYNC CHECK] Verify actual packets can round-trip, bypassing SW
        const { networkUtils } = await import('../utils/networkUtils.js');
        const isActuallyOnline = await networkUtils.checkConnectivity();
        if (!isActuallyOnline) {
            console.warn('[ShopDashboard] Pre-sync ping failed. Aborting sync to prevent stuck payloads.');
            return;
        }

        const currentUserId = coreState.activeUser?.id;
        if (!currentUserId) {
            console.warn('[ShopDashboard] Cannot sync offline logs — no active user.');
            return;
        }

        console.log(`[ShopDashboard] Syncing ${pending.length} offline logs (Network verified)...`);
        coreState.isSyncing = true; // [NEW] Lock for UI

        for (const log of pending) {
            try {
                // Guard: Only sync logs that belong to the current user
                if (!shouldSyncLog(log, currentUserId)) {
                    console.warn(`[ShopDashboard] Skipping log ${log.id} — belongs to user ${log.userId}, current user is ${currentUserId}`);
                    continue;
                }

                // Upload images if needed
                const finalImages = [];
                const post = log.posts[0];
                if (post.images) {
                    for (const img of post.images) {
                        if (img.isOffline && img.file) {
                            // [FAIL FAST] Apply a 15-second timeout to image uploads
                            const { networkUtils } = await import('../utils/networkUtils.js');
                            const uploadPromise = uploadFile(img.file);
                            const publicUrl = await networkUtils.withTimeout(
                                uploadPromise,
                                15000,
                                "Image upload timed out"
                            );
                            finalImages.push(publicUrl);
                        } else if (typeof img === 'string') {
                            finalImages.push(img);
                        } else if (img.url && !img.isOffline) {
                            finalImages.push(img.url);
                        }
                    }
                }

                // Build payload using STORED userId/shopId
                const payload = buildSyncPayload(log, currentUserId, finalImages);
                if (!payload) continue; // Extra safety

                // [FAIL FAST] Apply a 15s timeout to the final payload insert
                const { networkUtils } = await import('../utils/networkUtils.js');
                const insertPromise = supabase.from('logs').insert(payload).select().single();
                const { data, error } = await networkUtils.withTimeout(
                    insertPromise,
                    15000,
                    "Log insert payload timed out"
                );

                if (error) throw error;

                // Delete from offline storage
                await offlineStorage.deleteLog(log.id);
                console.log(`[ShopDashboard] Synced log: ${log.id} → ${data.id}`);
            } catch (e) {
                console.error("Sync failed for log:", log.id, e);

                const isTimeoutOrNetwork =
                    e.message?.includes('timed out') ||
                    e.message?.includes('Failed to fetch') ||
                    e.name === 'TypeError';

                if (isTimeoutOrNetwork) {
                    console.warn("[ShopDashboard] [FAIL FAST] Aborting sync loop. The payload is too large for the current active connection.");
                    break; // Exit the for...of loop entirely. Leave remaining in IDB.
                }
            }
        }

        coreState.isSyncing = false; // [NEW] Free the UI lock
        // Refresh feed after syncing to clear offline badges
        fetchFeed();
    }


    // --- Storage ---
    async function uploadFile(file, bucket = 'photos') {
        if (!file) throw new Error("No file provided");

        // Generate unique path: shopId/date-random-filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${coreState.currentShopId || 'common'}/${fileName}`;

        const { data, error } = await supabase.storage
            .from(bucket)
            .upload(filePath, file);

        if (error) {
            console.error("Upload Error:", error);
            throw error;
        }

        // Get Public URL
        const { data: { publicUrl } } = supabase.storage
            .from(bucket)
            .getPublicUrl(filePath);

        return publicUrl;
    }



    async function fetchShopNotifications(shopId) {
        if (!shopId) return;
        const { data, error } = await supabase
            .from('shop_notifications')
            .select('*')
            .eq('shop_id', shopId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error("[DashStore] Shop Notification Error:", error);
            return;
        }

        data.forEach(n => {
            const note = {
                id: n.id,
                userId: null, // It's a shop notification, no specific user owner
                shopId: n.shop_id,
                type: n.type,
                category: 'shop_alert',
                title: n.title,
                message: n.message,
                time: n.created_at,
                readStatus: n.read_status, // Special handling needed?
                payload: n.payload,
                isShopNotification: true
            };
            // We use the same central map, or a separate one?
            // Using central map for easy UI merging, but distinguished by flag.
            coreState.notifications.set(n.id, note);
        });
    }

    // --- Shop Context & Session (Moved from store.js) ---
    const sitesStore = useSitesStore(); // Access internal store instance

    function findShopMember(shopId, userId) {
        for (const m of coreState.members.values()) {
            if (m.shopId === shopId && m.userId === userId) return m;
        }
        return null;
    }

    function getShopForUser(userId) {
        for (const m of coreState.members.values()) {
            if (m.userId === userId) {
                return coreState.shops.get(m.shopId);
            }
        }
        return null;
    }

    async function switchShop(shopId) {
        // [Safety] Verify membership status
        if (coreState.activeUser) {
            const member = findShopMember(shopId, coreState.activeUser.id);
            // If member exists but is NOT active, block access (Leak Prevention)
            if (member && member.status && member.status.toLowerCase() !== 'active') {
                console.warn(`[ShopDashboard] Blocked access to inactive shop ${shopId}.`);
                return;
            }
            coreState.currentUserRole = member ? member.role : 'Visitor';
        }

        coreState.currentShopId = shopId;
        localStorage.setItem('lewotobi_shop_id', shopId); // Persist

        await sitesStore.fetchSites(shopId);

        // [NEW] Fetch Shop Notifications for Admins
        if (['Owner', 'Manager'].includes(coreState.currentUserRole)) {
            await fetchShopNotifications(shopId);
        }
    }

    async function loadShopContext(shopId) {
        let shop = coreState.shops.get(shopId);
        if (!shop && shopId !== 'landing-page-demo') {
            const { data, error } = await supabase.from('shops').select('*').eq('id', shopId).single();
            if (error) {
                console.error("[ShopDashboard] Load Shop Context Error:", error);
                // [FIX] Handle auth errors by logging out
                /* Auth error handled by interceptor */

            }
            if (data) {
                shop = new Shop(data);
                coreState.shops.set(shop.id, shop);
            }
        }
        if (shop) await switchShop(shopId);
        return shop;
    }

    return {
        // Context
        switchShop,
        loadShopContext,
        findShopMember,
        getShopForUser,
        loadShopWidgetData,
        rankSites,

        // Feed & Logs
        fetchFeed,
        saveLog,
        deleteLog,
        anonymizeLog,
        makeLogPrivate,
        makeLogPublic,
        hideLogFromFeed,
        unhideLogFromFeed,
        fetchHiddenLogs,
        uploadFile,
        fetchShopNotifications,
        syncLogs
    };

}

